// Code examples for SilentLoss API in 5 languages

export const paymentInitializedExamples = [
  {
    language: 'bash',
    code: `curl -X POST https://api.silentloss.io/api/v1/events/payment/initialized \\
  -H "Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: init-order-12345-1704067200" \\
  -d '{
    "payment_intent_id": "order_12345",
    "amount": 99.99,
    "currency": "USD",
    "provider": "stripe",
    "customer_email": "john@example.com",
    "product_name": "Annual Subscription",
    "metadata": {
      "plan": "professional",
      "billing_cycle": "yearly"
    }
  }'`
  },
  {
    language: 'php',
    code: `<?php

$apiKey = 'sl_abc123def456ghi789jkl012mno345pqr';
$baseUrl = 'https://api.silentloss.io/api/v1';

$data = [
    'payment_intent_id' => 'order_12345',
    'amount' => 99.99,
    'currency' => 'USD',
    'provider' => 'stripe',
    'customer_email' => 'john@example.com',
    'product_name' => 'Annual Subscription',
    'metadata' => [
        'plan' => 'professional',
        'billing_cycle' => 'yearly'
    ]
];

$idempotencyKey = 'init-' . $data['payment_intent_id'] . '-' . time();

$ch = curl_init($baseUrl . '/events/payment/initialized');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode($data),
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer ' . $apiKey,
        'Content-Type: application/json',
        'Idempotency-Key: ' . $idempotencyKey
    ],
    CURLOPT_TIMEOUT => 10
]);

$response = curl_exec($ch);
$statusCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$result = json_decode($response, true);

if ($statusCode === 200 && $result['success']) {
    echo "Payment initialized: " . $result['data']['payment_intent_id'];
} else {
    echo "Error: " . ($result['error']['message'] ?? 'Unknown error');
}`
  },
  {
    language: 'javascript',
    code: `const apiKey = 'sl_abc123def456ghi789jkl012mno345pqr';
const baseUrl = 'https://api.silentloss.io/api/v1';

const data = {
  payment_intent_id: 'order_12345',
  amount: 99.99,
  currency: 'USD',
  provider: 'stripe',
  customer_email: 'john@example.com',
  product_name: 'Annual Subscription',
  metadata: {
    plan: 'professional',
    billing_cycle: 'yearly'
  }
};

const idempotencyKey = \`init-\${data.payment_intent_id}-\${Date.now()}\`;

try {
  const response = await fetch(\`\${baseUrl}/events/payment/initialized\`, {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${apiKey}\`,
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey
    },
    body: JSON.stringify(data),
    signal: AbortSignal.timeout(10000)
  });

  const result = await response.json();
  
  if (result.success) {
    console.log('Payment initialized:', result.data.payment_intent_id);
  } else {
    console.error('Error:', result.error.message);
  }
} catch (error) {
  console.error('Request failed:', error.message);
}`
  },
  {
    language: 'python',
    code: `import requests
import time

api_key = 'sl_abc123def456ghi789jkl012mno345pqr'
base_url = 'https://api.silentloss.io/api/v1'

data = {
    'payment_intent_id': 'order_12345',
    'amount': 99.99,
    'currency': 'USD',
    'provider': 'stripe',
    'customer_email': 'john@example.com',
    'product_name': 'Annual Subscription',
    'metadata': {
        'plan': 'professional',
        'billing_cycle': 'yearly'
    }
}

idempotency_key = f"init-{data['payment_intent_id']}-{int(time.time())}"

headers = {
    'Authorization': f'Bearer {api_key}',
    'Content-Type': 'application/json',
    'Idempotency-Key': idempotency_key
}

try:
    response = requests.post(
        f'{base_url}/events/payment/initialized',
        json=data,
        headers=headers,
        timeout=10
    )
    
    result = response.json()
    
    if result.get('success'):
        print(f"Payment initialized: {result['data']['payment_intent_id']}")
    else:
        print(f"Error: {result.get('error', {}).get('message', 'Unknown error')}")
        
except requests.exceptions.RequestException as e:
    print(f"Request failed: {e}")`
  },
  {
    language: 'java',
    code: `import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import java.net.URI;
import java.time.Duration;
import org.json.JSONObject;

public class PaymentSafetyExample {
    private static final String API_KEY = "sl_abc123def456ghi789jkl012mno345pqr";
    private static final String BASE_URL = "https://api.silentloss.io/api/v1";

    public static void main(String[] args) throws Exception {
        JSONObject metadata = new JSONObject()
            .put("plan", "professional")
            .put("billing_cycle", "yearly");
            
        JSONObject data = new JSONObject()
            .put("payment_intent_id", "order_12345")
            .put("amount", 99.99)
            .put("currency", "USD")
            .put("provider", "stripe")
            .put("customer_email", "john@example.com")
            .put("product_name", "Annual Subscription")
            .put("metadata", metadata);

        String idempotencyKey = "init-order_12345-" + System.currentTimeMillis();

        HttpClient client = HttpClient.newBuilder()
            .connectTimeout(Duration.ofSeconds(10))
            .build();

        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(BASE_URL + "/events/payment/initialized"))
            .header("Authorization", "Bearer " + API_KEY)
            .header("Content-Type", "application/json")
            .header("Idempotency-Key", idempotencyKey)
            .POST(HttpRequest.BodyPublishers.ofString(data.toString()))
            .build();

        HttpResponse<String> response = client.send(request, 
            HttpResponse.BodyHandlers.ofString());

        JSONObject result = new JSONObject(response.body());
        
        if (result.getBoolean("success")) {
            System.out.println("Payment initialized: " + 
                result.getJSONObject("data").getString("payment_intent_id"));
        } else {
            System.err.println("Error: " + 
                result.getJSONObject("error").getString("message"));
        }
    }
}`
  },
  {
    language: 'csharp',
    code: `using System;
using System.Net.Http;
using System.Text;
using System.Text.Json;
using System.Threading.Tasks;

public class PaymentSafetyExample
{
    private const string ApiKey = "sl_abc123def456ghi789jkl012mno345pqr";
    private const string BaseUrl = "https://api.silentloss.io/api/v1";

    public static async Task Main()
    {
        var data = new
        {
            payment_intent_id = "order_12345",
            amount = 99.99,
            currency = "USD",
            provider = "stripe",
            customer_email = "john@example.com",
            product_name = "Annual Subscription",
            metadata = new
            {
                plan = "professional",
                billing_cycle = "yearly"
            }
        };

        var idempotencyKey = $"init-{data.payment_intent_id}-{DateTimeOffset.UtcNow.ToUnixTimeSeconds()}";

        using var client = new HttpClient { Timeout = TimeSpan.FromSeconds(10) };
        
        var request = new HttpRequestMessage(HttpMethod.Post, 
            $"{BaseUrl}/events/payment/initialized")
        {
            Content = new StringContent(
                JsonSerializer.Serialize(data),
                Encoding.UTF8,
                "application/json"
            )
        };
        
        request.Headers.Add("Authorization", $"Bearer {ApiKey}");
        request.Headers.Add("Idempotency-Key", idempotencyKey);

        var response = await client.SendAsync(request);
        var responseBody = await response.Content.ReadAsStringAsync();
        var result = JsonSerializer.Deserialize<JsonElement>(responseBody);

        if (result.GetProperty("success").GetBoolean())
        {
            Console.WriteLine($"Payment initialized: {result.GetProperty("data").GetProperty("payment_intent_id")}");
        }
        else
        {
            Console.WriteLine($"Error: {result.GetProperty("error").GetProperty("message")}");
        }
    }
}`
  }
];

export const providerRequestedExamples = [
  {
    language: 'bash',
    code: `curl -X POST https://api.silentloss.io/api/v1/events/payment/provider-requested \\
  -H "Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: req-order-12345-1704067210" \\
  -d '{
    "payment_intent_id": "order_12345",
    "provider": "stripe",
    "provider_request_id": "pi_3ABC123DEF456",
    "amount": 99.99,
    "currency": "USD",
    "payment_method": "card"
  }'`
  },
  {
    language: 'php',
    code: `<?php

$data = [
    'payment_intent_id' => 'order_12345',
    'provider' => 'stripe',
    'provider_request_id' => 'pi_3ABC123DEF456',
    'amount' => 99.99,
    'currency' => 'USD',
    'payment_method' => 'card'
];

$client->sendEvent('provider-requested', $data);`
  },
  {
    language: 'javascript',
    code: `const data = {
  payment_intent_id: 'order_12345',
  provider: 'stripe',
  provider_request_id: 'pi_3ABC123DEF456',
  amount: 99.99,
  currency: 'USD',
  payment_method: 'card'
};

await client.sendEvent('provider-requested', data);`
  },
  {
    language: 'python',
    code: `data = {
    'payment_intent_id': 'order_12345',
    'provider': 'stripe',
    'provider_request_id': 'pi_3ABC123DEF456',
    'amount': 99.99,
    'currency': 'USD',
    'payment_method': 'card'
}

client.send_event('provider-requested', data)`
  },
  {
    language: 'java',
    code: `JSONObject data = new JSONObject()
    .put("payment_intent_id", "order_12345")
    .put("provider", "stripe")
    .put("provider_request_id", "pi_3ABC123DEF456")
    .put("amount", 99.99)
    .put("currency", "USD")
    .put("payment_method", "card");

client.sendEvent("provider-requested", data);`
  },
  {
    language: 'csharp',
    code: `var data = new
{
    payment_intent_id = "order_12345",
    provider = "stripe",
    provider_request_id = "pi_3ABC123DEF456",
    amount = 99.99,
    currency = "USD",
    payment_method = "card"
};

await client.SendEventAsync("provider-requested", data);`
  }
];

export const providerSuccessExamples = [
  {
    language: 'bash',
    code: `curl -X POST https://api.silentloss.io/api/v1/events/payment/provider-success \\
  -H "Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: success-order-12345-1704067220" \\
  -d '{
    "payment_intent_id": "order_12345",
    "provider": "stripe",
    "provider_payment_id": "pi_3ABC123DEF456",
    "provider_transaction_id": "ch_3ABC123DEF456",
    "amount": 99.99,
    "currency": "USD",
    "provider_status": "succeeded",
    "provider_fee": 2.99,
    "net_amount": 97.00
  }'`
  },
  {
    language: 'php',
    code: `<?php

$data = [
    'payment_intent_id' => 'order_12345',
    'provider' => 'stripe',
    'provider_payment_id' => 'pi_3ABC123DEF456',    // REQUIRED!
    'provider_transaction_id' => 'ch_3ABC123DEF456', // REQUIRED!
    'amount' => 99.99,
    'currency' => 'USD',
    'provider_status' => 'succeeded',
    'provider_fee' => 2.99,
    'net_amount' => 97.00
];

$client->sendEvent('provider-success', $data);`
  },
  {
    language: 'javascript',
    code: `const data = {
  payment_intent_id: 'order_12345',
  provider: 'stripe',
  provider_payment_id: 'pi_3ABC123DEF456',    // REQUIRED!
  provider_transaction_id: 'ch_3ABC123DEF456', // REQUIRED!
  amount: 99.99,
  currency: 'USD',
  provider_status: 'succeeded',
  provider_fee: 2.99,
  net_amount: 97.00
};

await client.sendEvent('provider-success', data);`
  },
  {
    language: 'python',
    code: `data = {
    'payment_intent_id': 'order_12345',
    'provider': 'stripe',
    'provider_payment_id': 'pi_3ABC123DEF456',    # REQUIRED!
    'provider_transaction_id': 'ch_3ABC123DEF456', # REQUIRED!
    'amount': 99.99,
    'currency': 'USD',
    'provider_status': 'succeeded',
    'provider_fee': 2.99,
    'net_amount': 97.00
}

client.send_event('provider-success', data)`
  },
  {
    language: 'java',
    code: `JSONObject data = new JSONObject()
    .put("payment_intent_id", "order_12345")
    .put("provider", "stripe")
    .put("provider_payment_id", "pi_3ABC123DEF456")    // REQUIRED!
    .put("provider_transaction_id", "ch_3ABC123DEF456") // REQUIRED!
    .put("amount", 99.99)
    .put("currency", "USD")
    .put("provider_status", "succeeded")
    .put("provider_fee", 2.99)
    .put("net_amount", 97.00);

client.sendEvent("provider-success", data);`
  },
  {
    language: 'csharp',
    code: `var data = new
{
    payment_intent_id = "order_12345",
    provider = "stripe",
    provider_payment_id = "pi_3ABC123DEF456",    // REQUIRED!
    provider_transaction_id = "ch_3ABC123DEF456", // REQUIRED!
    amount = 99.99,
    currency = "USD",
    provider_status = "succeeded",
    provider_fee = 2.99,
    net_amount = 97.00
};

await client.SendEventAsync("provider-success", data);`
  }
];

export const providerFailedExamples = [
  {
    language: 'bash',
    code: `curl -X POST https://api.silentloss.io/api/v1/events/payment/provider-failed \\
  -H "Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: failed-order-12345-1704067230" \\
  -d '{
    "payment_intent_id": "order_12345",
    "provider": "stripe",
    "error_code": "card_declined",
    "error_message": "Your card has insufficient funds"
  }'`
  },
  {
    language: 'php',
    code: `<?php

$data = [
    'payment_intent_id' => 'order_12345',
    'provider' => 'stripe',
    'error_code' => 'card_declined',
    'error_message' => 'Your card has insufficient funds'
];

$client->sendEvent('provider-failed', $data);`
  },
  {
    language: 'javascript',
    code: `const data = {
  payment_intent_id: 'order_12345',
  provider: 'stripe',
  error_code: 'card_declined',
  error_message: 'Your card has insufficient funds'
};

await client.sendEvent('provider-failed', data);`
  },
  {
    language: 'python',
    code: `data = {
    'payment_intent_id': 'order_12345',
    'provider': 'stripe',
    'error_code': 'card_declined',
    'error_message': 'Your card has insufficient funds'
}

client.send_event('provider-failed', data)`
  },
  {
    language: 'java',
    code: `JSONObject data = new JSONObject()
    .put("payment_intent_id", "order_12345")
    .put("provider", "stripe")
    .put("error_code", "card_declined")
    .put("error_message", "Your card has insufficient funds");

client.sendEvent("provider-failed", data);`
  },
  {
    language: 'csharp',
    code: `var data = new
{
    payment_intent_id = "order_12345",
    provider = "stripe",
    error_code = "card_declined",
    error_message = "Your card has insufficient funds"
};

await client.SendEventAsync("provider-failed", data);`
  }
];

export const orderCreatedExamples = [
  {
    language: 'bash',
    code: `curl -X POST https://api.silentloss.io/api/v1/events/payment/order-created \\
  -H "Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: order-order-12345-1704067240" \\
  -d '{
    "payment_intent_id": "order_12345",
    "order_id": "ORD-2026-00123",
    "amount": 99.99,
    "currency": "USD",
    "order_status": "confirmed"
  }'`
  },
  {
    language: 'php',
    code: `<?php

$data = [
    'payment_intent_id' => 'order_12345',
    'order_id' => 'ORD-2026-00123',
    'amount' => 99.99,
    'currency' => 'USD',
    'order_status' => 'confirmed'
];

$client->sendEvent('order-created', $data);`
  },
  {
    language: 'javascript',
    code: `const data = {
  payment_intent_id: 'order_12345',
  order_id: 'ORD-2026-00123',
  amount: 99.99,
  currency: 'USD',
  order_status: 'confirmed'
};

await client.sendEvent('order-created', data);`
  },
  {
    language: 'python',
    code: `data = {
    'payment_intent_id': 'order_12345',
    'order_id': 'ORD-2026-00123',
    'amount': 99.99,
    'currency': 'USD',
    'order_status': 'confirmed'
}

client.send_event('order-created', data)`
  },
  {
    language: 'java',
    code: `JSONObject data = new JSONObject()
    .put("payment_intent_id", "order_12345")
    .put("order_id", "ORD-2026-00123")
    .put("amount", 99.99)
    .put("currency", "USD")
    .put("order_status", "confirmed");

client.sendEvent("order-created", data);`
  },
  {
    language: 'csharp',
    code: `var data = new
{
    payment_intent_id = "order_12345",
    order_id = "ORD-2026-00123",
    amount = 99.99,
    currency = "USD",
    order_status = "confirmed"
};

await client.SendEventAsync("order-created", data);`
  }
];

export const revenueRecordedExamples = [
  {
    language: 'bash',
    code: `curl -X POST https://api.silentloss.io/api/v1/events/payment/revenue-recorded \\
  -H "Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: revenue-order-12345-1704067250" \\
  -d '{
    "payment_intent_id": "order_12345",
    "revenue_amount": 99.99,
    "currency": "USD",
    "recorded_at": "2026-01-13T10:35:00Z"
  }'`
  },
  {
    language: 'php',
    code: `<?php

$data = [
    'payment_intent_id' => 'order_12345',
    'revenue_amount' => 99.99,
    'currency' => 'USD',
    'recorded_at' => date('c') // ISO 8601 format
];

$client->sendEvent('revenue-recorded', $data);`
  },
  {
    language: 'javascript',
    code: `const data = {
  payment_intent_id: 'order_12345',
  revenue_amount: 99.99,
  currency: 'USD',
  recorded_at: new Date().toISOString()
};

await client.sendEvent('revenue-recorded', data);`
  },
  {
    language: 'python',
    code: `from datetime import datetime

data = {
    'payment_intent_id': 'order_12345',
    'revenue_amount': 99.99,
    'currency': 'USD',
    'recorded_at': datetime.utcnow().isoformat() + 'Z'
}

client.send_event('revenue-recorded', data)`
  },
  {
    language: 'java',
    code: `import java.time.Instant;

JSONObject data = new JSONObject()
    .put("payment_intent_id", "order_12345")
    .put("revenue_amount", 99.99)
    .put("currency", "USD")
    .put("recorded_at", Instant.now().toString());

client.sendEvent("revenue-recorded", data);`
  },
  {
    language: 'csharp',
    code: `var data = new
{
    payment_intent_id = "order_12345",
    revenue_amount = 99.99,
    currency = "USD",
    recorded_at = DateTime.UtcNow.ToString("o")
};

await client.SendEventAsync("revenue-recorded", data);`
  }
];

export const batchEventsExamples = [
  {
    language: 'bash',
    code: `curl -X POST https://api.silentloss.io/api/v1/events/batch \\
  -H "Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr" \\
  -H "Content-Type: application/json" \\
  -d '{
    "events": [
      {
        "type": "payment.initialized",
        "payment_intent_id": "order_12345",
        "amount": 99.99,
        "currency": "USD",
        "provider": "stripe",
        "customer_email": "john@example.com"
      },
      {
        "type": "payment.provider_success",
        "payment_intent_id": "order_12345",
        "provider": "stripe",
        "provider_payment_id": "pi_3ABC123DEF456",
        "provider_transaction_id": "ch_3ABC123DEF456",
        "amount": 99.99,
        "currency": "USD",
        "provider_status": "succeeded"
      }
    ]
  }'`
  },
  {
    language: 'php',
    code: `<?php

$events = [
    [
        'type' => 'payment.initialized',
        'payment_intent_id' => 'order_12345',
        'amount' => 99.99,
        'currency' => 'USD',
        'provider' => 'stripe',
        'customer_email' => 'john@example.com'
    ],
    [
        'type' => 'payment.provider_success',
        'payment_intent_id' => 'order_12345',
        'provider' => 'stripe',
        'provider_payment_id' => 'pi_3ABC123DEF456',
        'provider_transaction_id' => 'ch_3ABC123DEF456',
        'amount' => 99.99,
        'currency' => 'USD',
        'provider_status' => 'succeeded'
    ]
];

$client->sendBatchEvents($events);`
  },
  {
    language: 'javascript',
    code: `const events = [
  {
    type: 'payment.initialized',
    payment_intent_id: 'order_12345',
    amount: 99.99,
    currency: 'USD',
    provider: 'stripe',
    customer_email: 'john@example.com'
  },
  {
    type: 'payment.provider_success',
    payment_intent_id: 'order_12345',
    provider: 'stripe',
    provider_payment_id: 'pi_3ABC123DEF456',
    provider_transaction_id: 'ch_3ABC123DEF456',
    amount: 99.99,
    currency: 'USD',
    provider_status: 'succeeded'
  }
];

await client.sendBatchEvents(events);`
  },
  {
    language: 'python',
    code: `events = [
    {
        'type': 'payment.initialized',
        'payment_intent_id': 'order_12345',
        'amount': 99.99,
        'currency': 'USD',
        'provider': 'stripe',
        'customer_email': 'john@example.com'
    },
    {
        'type': 'payment.provider_success',
        'payment_intent_id': 'order_12345',
        'provider': 'stripe',
        'provider_payment_id': 'pi_3ABC123DEF456',
        'provider_transaction_id': 'ch_3ABC123DEF456',
        'amount': 99.99,
        'currency': 'USD',
        'provider_status': 'succeeded'
    }
]

client.send_batch_events(events)`
  },
  {
    language: 'java',
    code: `JSONArray events = new JSONArray()
    .put(new JSONObject()
        .put("type", "payment.initialized")
        .put("payment_intent_id", "order_12345")
        .put("amount", 99.99)
        .put("currency", "USD")
        .put("provider", "stripe")
        .put("customer_email", "john@example.com"))
    .put(new JSONObject()
        .put("type", "payment.provider_success")
        .put("payment_intent_id", "order_12345")
        .put("provider", "stripe")
        .put("provider_payment_id", "pi_3ABC123DEF456")
        .put("provider_transaction_id", "ch_3ABC123DEF456")
        .put("amount", 99.99)
        .put("currency", "USD")
        .put("provider_status", "succeeded"));

client.sendBatchEvents(events);`
  },
  {
    language: 'csharp',
    code: `var events = new[]
{
    new
    {
        type = "payment.initialized",
        payment_intent_id = "order_12345",
        amount = 99.99,
        currency = "USD",
        provider = "stripe",
        customer_email = "john@example.com"
    },
    new
    {
        type = "payment.provider_success",
        payment_intent_id = "order_12345",
        provider = "stripe",
        provider_payment_id = "pi_3ABC123DEF456",
        provider_transaction_id = "ch_3ABC123DEF456",
        amount = 99.99,
        currency = "USD",
        provider_status = "succeeded"
    }
};

await client.SendBatchEventsAsync(events);`
  }
];

export const sdkExamplesNodeJS = `class PaymentSafetyClient {
  constructor(apiKey, baseUrl = 'https://api.silentloss.io/api/v1') {
    this.apiKey = apiKey;
    this.baseUrl = baseUrl;
  }

  async sendEvent(eventType, data) {
    const idempotencyKey = \`\${eventType}-\${data.payment_intent_id}-\${Date.now()}\`;
    
    const response = await fetch(\`\${this.baseUrl}/events/payment/\${eventType}\`, {
      method: 'POST',
      headers: {
        'Authorization': \`Bearer \${this.apiKey}\`,
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey
      },
      body: JSON.stringify(data),
      signal: AbortSignal.timeout(10000)
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(\`API Error: \${result.error.message}\`);
    }
    
    return result;
  }

  async paymentInitialized(data) {
    return this.sendEvent('initialized', data);
  }

  async providerRequested(data) {
    return this.sendEvent('provider-requested', data);
  }

  async providerSuccess(data) {
    return this.sendEvent('provider-success', data);
  }

  async providerFailed(data) {
    return this.sendEvent('provider-failed', data);
  }

  async orderCreated(data) {
    return this.sendEvent('order-created', data);
  }

  async revenueRecorded(data) {
    return this.sendEvent('revenue-recorded', data);
  }
}

// Usage
const client = new PaymentSafetyClient('sl_abc123...');

await client.paymentInitialized({
  payment_intent_id: 'order_12345',
  amount: 99.99,
  currency: 'USD',
  provider: 'stripe',
  customer_email: 'customer@example.com'
});`;

export const sdkExamplesPHP = `<?php

class PaymentSafetyClient
{
    private string $apiKey;
    private string $baseUrl;

    public function __construct(string $apiKey, string $baseUrl = 'https://api.silentloss.io/api/v1')
    {
        $this->apiKey = $apiKey;
        $this->baseUrl = $baseUrl;
    }

    public function sendEvent(string $eventType, array $data): array
    {
        $idempotencyKey = "{$eventType}-{$data['payment_intent_id']}-" . time();
        
        $ch = curl_init("{$this->baseUrl}/events/payment/{$eventType}");
        curl_setopt_array($ch, [
            CURLOPT_RETURNTRANSFER => true,
            CURLOPT_POST => true,
            CURLOPT_POSTFIELDS => json_encode($data),
            CURLOPT_HTTPHEADER => [
                "Authorization: Bearer {$this->apiKey}",
                "Content-Type: application/json",
                "Idempotency-Key: {$idempotencyKey}"
            ],
            CURLOPT_TIMEOUT => 10
        ]);

        $response = curl_exec($ch);
        $statusCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
        curl_close($ch);

        $result = json_decode($response, true);

        if ($statusCode !== 200 || !$result['success']) {
            throw new Exception("API Error: " . ($result['error']['message'] ?? 'Unknown error'));
        }

        return $result;
    }

    public function paymentInitialized(array $data): array
    {
        return $this->sendEvent('initialized', $data);
    }

    public function providerSuccess(array $data): array
    {
        return $this->sendEvent('provider-success', $data);
    }

    // ... other methods
}

// Usage
$client = new PaymentSafetyClient('sl_abc123...');

$client->paymentInitialized([
    'payment_intent_id' => 'order_12345',
    'amount' => 99.99,
    'currency' => 'USD',
    'provider' => 'stripe',
    'customer_email' => 'customer@example.com'
]);`;
